import { downloadPatientBioCsv } from "@/api/adapters/patients";
import { useIsEmrSession } from "@/app/store/auth.store";
import { useBiometric } from "@/shared/hooks/use-biometric";
import {
	BarElement,
	CategoryScale,
	Chart as ChartJS,
	Legend,
	LinearScale,
	LineElement,
	PointElement,
	Title,
	Tooltip,
} from "chart.js";
import { Clock, Download, Footprints, HeartPulse, Moon } from "lucide-react";
import { useMemo, useState } from "react";
import { Bar, Line } from "react-chartjs-2";
import { useTranslation } from "react-i18next";

ChartJS.register(
	CategoryScale,
	LinearScale,
	BarElement,
	LineElement,
	PointElement,
	Title,
	Tooltip,
	Legend,
);

import type { PatientListItem } from "@/api/adapters/patients";
import {
	type BioHeartRate,
	bioPeriodLabel,
	densifyByDay,
	hrBandColor,
	hrBandKey,
	isHrAbnormal,
	todayIso,
} from "@/features/patients/bio-data";

type Period = "7" | "30" | "90" | "all";

interface Props {
	patient: PatientListItem;
}

export function BiometricTab({ patient }: Props) {
	const { t } = useTranslation(["medical"]);
	const [period, setPeriod] = useState<Period>("30");
	const isEmr = useIsEmrSession();

	const { data: bio, isError } = useBiometric(patient);

	const days =
		period === "7" ? 7 : period === "30" ? 30 : period === "90" ? 90 : 9999;

	// The window every series on this tab is measured against: `days` calendar days ending today,
	// floored at the discharge date (SHCT-565). Computed once so the charts and the 기술문 beside
	// them can never describe different spans.
	const window = useMemo(
		() => ({ today: todayIso(), floor: patient.dischargeDate }),
		[patient.dischargeDate],
	);

	// Chart series: one slot per calendar day so missing dates show as gaps, not a
	// collapsed axis (SHCT-565). Left-joins the sparse BE series onto a continuous window.
	const stepsDense = useMemo(
		() => densifyByDay(bio?.steps ?? [], days, window),
		[bio, days, window],
	);
	const sleepDense = useMemo(
		() => densifyByDay(bio?.sleep ?? [], days, window),
		[bio, days, window],
	);

	// Summary cards + 기술문 count real readings only — but from the SAME window the charts draw,
	// which is what PRD 04 §3.3 means by "{기간} 기준". They used to come from `sliceFor`, which
	// takes the last N *readings* rather than N days, so a sparse device made the statement describe
	// a different (and much longer) span than the chart printed directly above it.
	const filtered = useMemo(() => {
		const real = <T,>(slots: { entry: T | null }[]) =>
			slots.map((s) => s.entry).filter((e): e is T => e != null);
		return {
			steps: real(stepsDense),
			sleep: real(sleepDense),
			heartRate: real(densifyByDay(bio?.heartRate ?? [], days, window)),
		};
	}, [bio, days, window, stepsDense, sleepDense]);
	// SHCT-675 — the one step goal this render judges against, for BOTH the bars and the sentence.
	// Resolved from `filtered.steps` (the real readings of this window) rather than from the dense
	// slots, so it is the identical input the sentence helper receives and the two cannot disagree.
	const stepGoal = useMemo(() => resolveStepGoal(filtered.steps), [filtered.steps]);

	// Heart rate is the exception (SHCT-677): plot ONLY the days that have a reading,
	// so an isolated reading between empty days joins its neighbours instead of being
	// stranded as a lone dot by `spanGaps: false`. This does not walk back SHCT-565 —
	// that ticket scopes the continuous axis to 걸음수 + 수면 ("Applies to Step count and
	// Sleep time"); the HR line was densified alongside them only for visual consistency.
	//
	// Still built from `densifyByDay` and then filtered: densifying first applies the calendar
	// window, and the filter then drops the empty slots inside it.
	const hrPoints = useMemo(
		() => densifyByDay(bio?.heartRate ?? [], days, window).filter((x) => x.entry),
		[bio, days, window],
	);

	// Summary stats
	const avgSteps = useMemo(
		() =>
			filtered.steps.length
				? Math.round(
						filtered.steps.reduce((s, x) => s + x.value, 0) /
							filtered.steps.length,
					)
				: 0,
		[filtered.steps],
	);

	const avgSleep = useMemo(
		() =>
			filtered.sleep.length
				? (
						filtered.sleep.reduce((s, x) => s + x.value, 0) /
						filtered.sleep.length
					).toFixed(1)
				: "0.0",
		[filtered.sleep],
	);

	const avgHr = useMemo(
		() =>
			filtered.heartRate.length
				? Math.round(
						filtered.heartRate.reduce((s, x) => s + x.value, 0) /
							filtered.heartRate.length,
					)
				: null,
		[filtered.heartRate],
	);

	const hrRange = useMemo(() => {
		if (!filtered.heartRate.length) return null;
		const vals = filtered.heartRate.map((x) => x.value);
		return { min: Math.min(...vals), max: Math.max(...vals) };
	}, [filtered.heartRate]);

	const latestHr = filtered.heartRate[filtered.heartRate.length - 1];
	const latestHrKey = latestHr ? hrBandKey(latestHr.band) : null;
	const latestHrLabel = latestHrKey
		? t(`medical:patients.biometric.hr.${latestHrKey}`)
		: "—";
	// PRD §3.3 v2.0: 서맥/빈맥 배지는 최신(latest) 안정시 심박수가 정상범위를 이탈했을 때만 노출.
	const latestHrAbnormal = latestHr ? isHrAbnormal(latestHr.band) : false;

	// Sync status — stale flag is BE-authoritative (syncStale), not re-derived here.
	const syncLabel = (() => {
		if (!bio?.lastSync) return null;
		const syncDate = new Date(bio.lastSync);
		if (Number.isNaN(syncDate.getTime())) return null;
		const today = new Date();
		const diffDays = Math.floor(
			(today.getTime() - syncDate.getTime()) / (24 * 3600 * 1000),
		);
		const dateStr = `${syncDate.getFullYear()}.${String(syncDate.getMonth() + 1).padStart(2, "0")}.${String(syncDate.getDate()).padStart(2, "0")} ${String(syncDate.getHours()).padStart(2, "0")}:${String(syncDate.getMinutes()).padStart(2, "0")}`;
		const rel =
			diffDays <= 0
				? t("medical:patients.biometric.sync.today")
				: diffDays === 1
					? t("medical:patients.biometric.sync.yesterday")
					: t("medical:patients.biometric.sync.daysAgo", { count: diffDays });
		return { dateStr, rel, stale: bio.syncStale };
	})();

	return (
		<div>
			{/* ── Period filter + sync + CSV ── */}
			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					marginBottom: 16,
					gap: 12,
				}}
			>
				<div className="tab-group">
					{(["7", "30", "90", "all"] as Period[]).map((p) => (
						<button
							key={p}
							type="button"
							className={`tab-group__item${period === p ? " tab-group__item--active" : ""}`}
							onClick={() => setPeriod(p)}
						>
							{t(`medical:patients.biometric.period.${bioPeriodLabel(p)}`)}
						</button>
					))}
				</div>
				<div
					style={{
						display: "flex",
						alignItems: "center",
						gap: 12,
						marginLeft: "auto",
					}}
				>
					{syncLabel && (
						<span
							className={`bio-sync${syncLabel.stale ? " bio-sync--stale" : ""}`}
							title={
								syncLabel.stale
									? t("medical:patients.biometric.sync.delayed")
									: ""
							}
						>
							<Clock size={13} />
							{t("medical:patients.biometric.sync.label", {
								date: syncLabel.dateStr,
								rel: syncLabel.rel,
							})}
						</span>
					)}
					{/* SHCT-994: an EMR session may not export (plan D9⑤). */}
					{!isEmr && (
						<button
							type="button"
							className="btn btn--secondary btn--sm"
							onClick={() => downloadPatientBioCsv(patient, period)}
						>
							<Download size={14} style={{ marginRight: 4 }} />
							CSV
						</button>
					)}
				</div>
			</div>

			{/* PRD §8.2: 생체 데이터 동기화 실패 안내 (직전 데이터는 유지) */}
			{isError && (
				<div className="bio-error" role="alert">
					{t("medical:patients.errors.loadBiometric")}
				</div>
			)}

			{/* ── Summary cards ── */}
			<div className="bio-summary-grid">
				<div className="card bio-summary-card">
					<div>
						<div className="bio-summary__label">
							{t("medical:patients.biometric.summary.avgSteps")}
						</div>
						<div className="bio-summary__value">
							{avgSteps.toLocaleString()}{" "}
							<span style={{ fontSize: 12 }}>
								{t("medical:patients.biometric.summary.stepsUnit")}
							</span>
						</div>
					</div>
					<div
						className="bio-summary__icon"
						style={{ background: "#eff6ff", color: "#3b82f6" }}
					>
						<Footprints size={22} />
					</div>
				</div>
				<div className="card bio-summary-card">
					<div>
						<div className="bio-summary__label">
							{t("medical:patients.biometric.summary.avgSleep")}
						</div>
						<div className="bio-summary__value">
							{avgSleep}{" "}
							<span style={{ fontSize: 12 }}>
								{t("medical:patients.biometric.summary.hoursUnit")}
							</span>
						</div>
					</div>
					<div
						className="bio-summary__icon"
						style={{ background: "#f3e8ff", color: "#8b5cf6" }}
					>
						<Moon size={22} />
					</div>
				</div>
				<div className="card bio-summary-card">
					<div>
						{/* PRD §3.3 v2.0 / prototype: 최근(latest) 안정시 심박수 + 낮음·정상·높음(정상 시) /
						    서맥·빈맥 배지(이탈 시). */}
						<div className="bio-summary__label">
							{t("medical:patients.biometric.summary.latestHr")}
						</div>
						<div className="bio-summary__value">
							{latestHr ? latestHr.value : "—"}{" "}
							<span style={{ fontSize: 12 }}>
								{t("medical:patients.biometric.summary.bpmUnit")}
							</span>
							{latestHr &&
								(latestHrAbnormal ? (
									<span className="bio-hr-badge">{latestHrLabel}</span>
								) : (
									<span
										style={{
											fontSize: 12,
											marginLeft: 6,
											color: hrBandColor(latestHr.band),
										}}
									>
										{latestHrLabel}
									</span>
								))}
						</div>
					</div>
					<div
						className="bio-summary__icon"
						style={{ background: "#fee2e2", color: "#ef4444" }}
					>
						<HeartPulse size={22} />
					</div>
				</div>
			</div>

			{/* ── Steps chart ── */}
			<div className="card">
				<div className="card__header bio-card__header">
					<h3 className="card__title">
						{t("medical:patients.biometric.chartTitle.steps")}
					</h3>
					<div className="bio-stmt">
						<Footprints size={15} />
						<span>{_stepStatement(filtered.steps, period, t)}</span>
					</div>
				</div>
				<div className="card__body">
					<div style={{ height: 280 }}>
						<Bar
							data={{
								labels: stepsDense.map((x) => x.date.slice(5)),
								datasets: [
									{
										label: t("medical:patients.biometric.charts.steps"),
										data: stepsDense.map((x) => x.entry?.value ?? null),
										// SHCT-675 — coloured against the ADMIN-CONFIGURED goal, resolved by the
										// same helper the sentence directly above this chart uses. It used to
										// compare against a literal 7000, so a hospital configuring any other
										// step goal got a chart that contradicted its own caption: the
										// sentence could report the goal met while the bars beneath it were
										// amber for the same days.
										//
										// With no reference row configured (goal == null) every bar stays
										// neutral. That matches what the sentence does in the same case — it
										// renders a dash rather than a verdict — because there is no
										// threshold to have missed. Falling back to 7000 here would restore
										// the exact defect this removes.
										backgroundColor: stepsDense.map((x) =>
											stepGoal != null && x.entry && x.entry.value < stepGoal
												? "#f59e0b"
												: "#3b82f6",
										),
									},
								],
							}}
							options={{ responsive: true, maintainAspectRatio: false }}
						/>
					</div>
				</div>
			</div>

			{/* ── Sleep chart ── */}
			<div className="card" style={{ marginTop: 16 }}>
				<div className="card__header bio-card__header">
					<h3 className="card__title">
						{t("medical:patients.biometric.chartTitle.sleep")}
					</h3>
					<div className="bio-stmt">
						<Moon size={15} />
						<span>{_sleepStatement(filtered.sleep, period, t)}</span>
					</div>
				</div>
				<div className="card__body">
					<div style={{ height: 280 }}>
						<Bar
							data={{
								labels: sleepDense.map((x) => x.date.slice(5)),
								datasets: [
									{
										label: t("medical:patients.biometric.charts.deepSleep"),
										data: sleepDense.map((x) => x.entry?.deep ?? null),
										backgroundColor: "#1e40af",
									},
									{
										label: t("medical:patients.biometric.charts.lightSleep"),
										data: sleepDense.map((x) => x.entry?.light ?? null),
										backgroundColor: "#93c5fd",
									},
									{
										label: "REM",
										data: sleepDense.map((x) => x.entry?.rem ?? null),
										backgroundColor: "#c084fc",
									},
									{
										// The fourth band used to be mid-session waking. It now carries
										// hand-typed nights, which have no stage split and would otherwise
										// draw nothing at all. Same slate as the patient app's own chart, so
										// one night looks the same to the clinician and to the patient.
										label: t("medical:patients.biometric.charts.manualEntry"),
										data: sleepDense.map((x) => x.entry?.manual ?? null),
										backgroundColor: "#7A8AA0",
									},
								],
							}}
							options={{
								responsive: true,
								maintainAspectRatio: false,
								scales: { x: { stacked: true }, y: { stacked: true, max: 12 } },
							}}
						/>
					</div>
				</div>
			</div>

			{/* ── Resting heart-rate chart ── */}
			<div className="card" style={{ marginTop: 16 }}>
				<div className="card__header bio-card__header">
					<h3 className="card__title">
						{t("medical:patients.biometric.chartTitle.heartRate")}
						{/* PRD §3.3 v2.0: 카드 제목 옆 서맥/빈맥 배지 (최신 이탈 시) */}
						{latestHrAbnormal && (
							<span className="bio-hr-badge">{latestHrLabel}</span>
						)}
					</h3>
					<div className="bio-stmt">
						<HeartPulse size={15} />
						<span>{_hrStatement(filtered.heartRate, period, t)}</span>
					</div>
				</div>
				<div className="card__body">
					{/* PRD §3.3 / prototype: 평균·최근·범위(min~max) 3-box 요약 */}
					<div className="hr-info-grid">
						<div className="hr-info-box">
							<div className="hr-info-box__label">
								{t("medical:patients.biometric.hrInfo.avg")}
							</div>
							<div className="hr-info-box__value">
								{avgHr ?? "—"} <span>bpm</span>
							</div>
						</div>
						<div className="hr-info-box">
							<div className="hr-info-box__label">
								{t("medical:patients.biometric.hrInfo.latest")}
							</div>
							<div
								className="hr-info-box__value"
								style={{ color: hrBandColor(latestHr?.band ?? null) }}
							>
								{latestHr ? latestHr.value : "—"} <span>bpm</span>
							</div>
						</div>
						<div className="hr-info-box">
							<div className="hr-info-box__label">
								{t("medical:patients.biometric.hrInfo.range")}
							</div>
							<div className="hr-info-box__value">
								{hrRange ? `${hrRange.min}~${hrRange.max}` : "—"} <span>bpm</span>
							</div>
						</div>
					</div>
					<div style={{ height: 280 }}>
						<Line
							data={{
								labels: hrPoints.map((x) => x.date.slice(5)),
								datasets: [
									{
										type: "line" as const,
										label: t("medical:patients.biometric.charts.heartRate"),
										// Every slot in `hrPoints` carries a reading, so no null ever reaches
										// the dataset and `spanGaps` has nothing left to decide (SHCT-677).
										data: hrPoints.map((x) => x.entry?.value ?? null),
										borderColor: "#ef4444",
										backgroundColor: "rgba(239,68,68,0.08)",
										fill: true,
										pointBackgroundColor: hrPoints.map((x) =>
											hrBandColor(x.entry?.band ?? null),
										),
										pointRadius: hrPoints.map((x) =>
											x.entry && isHrAbnormal(x.entry.band) ? 4 : 2,
										),
										tension: 0.3,
									},
								],
							}}
							options={{ responsive: true, maintainAspectRatio: false }}
						/>
					</div>
				</div>
			</div>
		</div>
	);
}

// ── Statement helpers (matching prototype _renderBioStatements) ──

function _cntEl(
	n: number,
	t: (k: string, opts?: Record<string, unknown>) => string,
) {
	// The count and its unit are one phrase, resolved together: Korean writes "3일" with no space,
	// English needs "3 days" — and "1 day". Concatenating a bare unit produced "1days" on screen.
	const label = t("medical:patients.biometric.statement.dayCount", { count: n });
	if (n === 0) return <strong style={{ color: "#10b981" }}>{label}</strong>;
	return <strong style={{ color: "#f59e0b" }}>{label}</strong>;
}

/**
 * The daily step goal in force for a period — the administrator-configured STEP_COUNT normal minimum,
 * carried on every point the server returns. Every point in a period carries the SAME value, so the
 * first non-null one is the period's goal.
 *
 * `null` means no reference row is configured, and that is a real answer rather than a missing one:
 * there is nothing to judge a day against. Callers must render something neutral, never fall back to a
 * literal — a literal is precisely the defect SHCT-675 removes, and it makes the screen assert a
 * threshold no administrator ever set.
 *
 * Shared by the bar colouring and the sentence above it deliberately. Those two read the same number
 * and are 160 lines apart; when the bars kept their own copy (a hardcoded 7000) the sentence could say
 * a patient met their goal while the bars beneath it were coloured as misses.
 */
function resolveStepGoal(steps: { goal: number | null }[]): number | null {
	return steps.find((x) => x.goal != null)?.goal ?? null;
}

function _stepStatement(
	steps: { value: number; goal: number | null }[],
	period: string,
	t: (k: string, opts?: Record<string, unknown>) => string,
): React.ReactNode {
	const label = t(
		`medical:patients.biometric.period.${bioPeriodLabel(period)}`,
	);
	const goalValue = resolveStepGoal(steps);
	if (goalValue == null) return <>{label} —</>;
	const goal = t("medical:patients.biometric.statement.stepsGoal", {
		value: goalValue.toLocaleString(),
	});
	const miss = steps.filter((x) => x.value < goalValue).length;
	if (miss === 0)
		return (
			<>
				{label} {goal}{" "}
				<strong style={{ color: "#10b981" }}>
					{t("medical:patients.biometric.statement.stepsMet")}
				</strong>
			</>
		);
	return (
		<>
			{label} {goal} {_cntEl(miss, t)}{" "}
			{t("medical:patients.biometric.statement.stepsUnmet")}
		</>
	);
}

function _sleepStatement(
	sleep: { value: number; normalMin: number | null; normalMax: number | null }[],
	period: string,
	t: (k: string, opts?: Record<string, unknown>) => string,
): React.ReactNode {
	const label = t(
		`medical:patients.biometric.period.${bioPeriodLabel(period)}`,
	);
	// The normal range is admin-configured (SLEEP_DURATION normalMin/normalMax) — every point in a period
	// carries the same range, so the first non-null one is the period's range.
	const withRange = sleep.find((x) => x.normalMin != null && x.normalMax != null);
	if (!withRange) return <>{label} —</>;
	const range = t("medical:patients.biometric.statement.sleepRange", {
		min: withRange.normalMin,
		max: withRange.normalMax,
	});
	const low = sleep.filter((x) => x.value < (withRange.normalMin as number)).length;
	const high = sleep.filter((x) => x.value > (withRange.normalMax as number)).length;
	if (low === 0 && high === 0)
		return (
			<>
				{label} {range}{" "}
				<strong style={{ color: "#10b981" }}>
					{t("medical:patients.biometric.statement.sleepMet")}
				</strong>
			</>
		);
	return (
		<>
			{label} {range} {t("medical:patients.biometric.statement.sleepUnmet")}{" "}
			{_cntEl(low, t)} · {t("medical:patients.biometric.statement.sleepOver")}{" "}
			{_cntEl(high, t)}
		</>
	);
}

function _hrStatement(
	hr: BioHeartRate[],
	period: string,
	t: (k: string, opts?: Record<string, unknown>) => string,
): React.ReactNode {
	const label = t(
		`medical:patients.biometric.period.${bioPeriodLabel(period)}`,
	);
	const range = t("medical:patients.biometric.statement.hrRange");
	const abn = hr.filter((x) => isHrAbnormal(x.band)).length;
	const latest = hr.length ? hr[hr.length - 1] : null;
	const clsKey = latest ? hrBandKey(latest.band) : null;
	const cls = clsKey ? t(`medical:patients.biometric.hr.${clsKey}`) : "-";
	return (
		<>
			{label} {range} {t("medical:patients.biometric.statement.hrOut")}{" "}
			{_cntEl(abn, t)} · {t("medical:patients.biometric.statement.hrCurrent")}{" "}
			<strong style={{ color: hrBandColor(latest?.band ?? null) }}>{cls}</strong>
		</>
	);
}
